import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from "@heroicons/react/24/outline"
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Button, Input } from "~/ui-rtk/components/ui/controls"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useConnect from "./connect"

function parseUtmPairs(urlTags: string): { key: string; value: string }[] {
  if (!urlTags || urlTags === "[]" || urlTags === "") return []
  try {
    const parsed = JSON.parse(urlTags)
    if (Array.isArray(parsed)) {
      return parsed as { key: string; value: string }[]
    }
  } catch {
    return urlTags
      .split("&")
      .filter(Boolean)
      .map(pair => {
        const [key = "", value = ""] = pair.split("=")
        return { key, value }
      })
  }
  return []
}

function utmToQueryString(urlTags: string): string {
  return parseUtmPairs(urlTags)
    .map(({ key, value }) => `${key}=${value}`)
    .join("&")
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied to clipboard`),
    () => toast.error("Failed to copy to clipboard"),
  )
}

function UtmPreview({ urlTags }: { urlTags: string }) {
  const pairs = parseUtmPairs(urlTags)

  if (pairs.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-system-error-500/40 bg-system-error-500/10 px-2 py-0.5 text-xs font-medium text-system-error-300">
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        No UTM set
      </span>
    )
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {pairs.map(({ key, value }) => (
        <span
          key={`${key}=${value}`}
          className="inline-flex items-center gap-1 rounded-md border border-system-blue-600/40 bg-system-blue-700/40 px-2 py-0.5 font-mono text-xs"
        >
          <span className="text-system-gray-400">{key}</span>
          <span className="text-system-gray-500">=</span>
          <span className="max-w-[14rem] truncate text-white" title={value}>
            {value}
          </span>
        </span>
      ))}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-6 w-64 animate-pulse rounded bg-system-blue-600/40" />
      <div className="h-96 animate-pulse rounded-xl border border-system-blue-600/40 bg-system-blue-800/60" />
    </div>
  )
}

export default function UtmAuditPage() {
  const {
    campaignRows,
    isLoading,
    defaultUtm,
    setDefaultUtm,
    getAdIdsForCampaignUtm,
    getAllAdIdsWithoutDefaultUtm,
  } = useConnect()

  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    document.title = "Marathon Data: Audits > UTM Audit"
  }, [])

  const handleCopyRowAdIds = useCallback(
    (campaignId: string, urlTags: string) => {
      const adIds = getAdIdsForCampaignUtm(campaignId, urlTags)
      if (adIds.length === 0) {
        toast.warn("No ad IDs found for this campaign")
        return
      }
      copyToClipboard(adIds.join(", "), `${adIds.length} ad ID(s)`)
    },
    [getAdIdsForCampaignUtm],
  )

  const handleCopyAllAdIds = useCallback(() => {
    if (!defaultUtm) {
      toast.warn("Please select a default UTM first")
      return
    }
    const adIds = getAllAdIdsWithoutDefaultUtm()
    if (adIds.length === 0) {
      toast.info("All ads already have the default UTM")
      return
    }
    copyToClipboard(adIds.join(", "), `${adIds.length} ad ID(s)`)
  }, [defaultUtm, getAllAdIdsWithoutDefaultUtm])

  const defaultUtmQueryString = useMemo(
    () => utmToQueryString(defaultUtm),
    [defaultUtm],
  )

  const visibleCampaignRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    return campaignRows
      .filter(row => row.urlTags !== defaultUtm)
      .filter(row => {
        if (!search) return true
        return (
          row.campaignName.toLowerCase().includes(search) ||
          row.urlTags.toLowerCase().includes(search)
        )
      })
  }, [campaignRows, defaultUtm, searchTerm])

  return (
    <div className="min-h-screen">
      <div className="text-start mb-6">
        <Breadcrumbs
          items={[
            { slug: "/audits", name: "Audits" },
            { slug: "utm", name: "UTM Audit" },
          ]}
        />
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">UTM Audit</h1>
        <p className="mt-2 max-w-2xl text-sm text-system-gray-400 leading-relaxed">
          Review Meta ads running with inconsistent URL tags. Star the UTM
          convention you want to standardize on, then copy the mismatched ad IDs
          into Meta Ads Manager to bulk-edit URL parameters.
        </p>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="space-y-6 animate-fadeIn">
          {defaultUtm && (
            <div className="flex flex-col gap-4 rounded-xl border border-system-brand-pink-500/40 bg-gradient-to-br from-system-brand-pink-600/10 to-system-sea-600/10 p-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-system-brand-pink-300">
                  <CheckCircleIcon className="h-4 w-4" />
                  Default UTM
                </h3>
                <UtmPreview urlTags={defaultUtm} />
              </div>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant={{ variant: "solid", color: "pink" }}
                  onClick={() =>
                    copyToClipboard(defaultUtmQueryString, "Default UTM")
                  }
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy UTM query string
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={{ variant: "outlined" }}
                  onClick={handleCopyAllAdIds}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy mismatched ad IDs
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={{ variant: "text" }}
                  onClick={() => setDefaultUtm("")}
                >
                  Clear default
                </Button>
              </div>
            </div>
          )}

          <section>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="h-6 w-1 rounded-full bg-system-brand-pink-500" />
                <h2 className="text-xl font-semibold text-white">
                  {defaultUtm
                    ? "Campaigns not matching the default"
                    : "Campaigns & URL tags"}
                </h2>
                <span className="ml-1 rounded-full bg-system-blue-700/60 px-2 py-0.5 text-xs font-medium text-system-gray-300">
                  {visibleCampaignRows.length}
                </span>
              </div>
              <div className="w-full sm:max-w-xs">
                <Input
                  fullWidth
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search campaigns or UTMs…"
                  prependNode={
                    <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-system-gray-500" />
                  }
                />
              </div>
            </div>

            {!defaultUtm && visibleCampaignRows.length > 0 && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-system-blue-600/30 bg-system-blue-800/40 px-4 py-3 text-sm text-system-gray-300">
                <StarIcon className="h-4 w-4 shrink-0 text-system-gray-400" />
                Click{" "}
                <span className="font-medium text-white">
                  Set as default
                </span>{" "}
                on a row to mark it as your standard UTM convention. Campaigns
                that don't match will be highlighted for review.
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-system-blue-600/40 bg-system-blue-800/40">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-system-blue-600/40 bg-system-blue-800/80">
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-system-gray-400">
                        Campaign
                      </th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-system-gray-400">
                        Current URL tags
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-system-gray-400">
                        Ads
                      </th>
                      <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-system-gray-400">
                        Copy ad IDs
                      </th>
                      <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-system-gray-400">
                        Make default
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCampaignRows.map((row, idx) => {
                      const isRowDefault = row.urlTags === defaultUtm
                      const hasPairs = parseUtmPairs(row.urlTags).length > 0
                      return (
                        <tr
                          key={`${row.campaignId}-${row.urlTags}`}
                          className={cn(
                            "border-b border-system-blue-600/20 transition-colors hover:bg-system-blue-700/30",
                            idx === visibleCampaignRows.length - 1 &&
                              "border-b-0",
                          )}
                        >
                          <td className="px-5 py-4 align-top">
                            <p className="font-medium text-white">
                              {row.campaignName}
                            </p>
                            <p className="mt-0.5 font-mono text-xs text-system-gray-500">
                              {row.campaignId}
                            </p>
                          </td>
                          <td className="px-5 py-4 align-top">
                            <UtmPreview urlTags={row.urlTags} />
                            {hasPairs && (
                              <button
                                type="button"
                                onClick={() =>
                                  copyToClipboard(
                                    utmToQueryString(row.urlTags),
                                    "UTMs",
                                  )
                                }
                                className="mt-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-system-gray-400 transition-colors hover:bg-system-blue-700/40 hover:text-white"
                              >
                                <ClipboardDocumentIcon className="h-3 w-3" />
                                Copy UTMs
                              </button>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right align-top text-white tabular-nums">
                            {row.adCount.toLocaleString()}
                          </td>
                          <td className="px-5 py-4 text-right align-top">
                            <Button
                              type="button"
                              size="xs"
                              variant={{ variant: "solid", color: "pink" }}
                              onClick={() =>
                                handleCopyRowAdIds(row.campaignId, row.urlTags)
                              }
                              className="whitespace-nowrap"
                            >
                              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                              Copy {row.adCount} ID
                              {row.adCount !== 1 ? "s" : ""}
                            </Button>
                          </td>
                          <td className="w-16 px-3 py-4 text-center align-top">
                            <button
                              type="button"
                              onClick={() =>
                                setDefaultUtm(isRowDefault ? "" : row.urlTags)
                              }
                              disabled={!hasPairs}
                              title={
                                !hasPairs
                                  ? "Cannot set an empty UTM as default"
                                  : isRowDefault
                                    ? "Unset default"
                                    : "Set as default"
                              }
                              className={cn(
                                "rounded-md p-1.5 transition-colors",
                                isRowDefault
                                  ? "text-system-brand-pink-400 hover:text-system-brand-pink-300"
                                  : "text-system-gray-500 hover:text-system-gray-300 disabled:opacity-40 disabled:hover:text-system-gray-500",
                              )}
                            >
                              {isRowDefault ? (
                                <StarIconSolid className="h-4 w-4" />
                              ) : (
                                <StarIcon className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                    {visibleCampaignRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-5 py-10 text-center text-sm text-system-gray-400"
                        >
                          {searchTerm
                            ? "No campaigns match your search."
                            : defaultUtm
                              ? "All campaigns match the default UTM."
                              : "No Meta ad data with spend in the last 30 days."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
